import {
  readSessionStoredString,
  readStoredRecord,
} from '../utils/storage';
import { isNetworkAuthError } from 'src/firebase/authErrors';
import { openBootWindow, recordBootAuthPending, recordBootAuthResolved, recordBootAuthSettled, type tBootOutcome } from 'src/firebase/bootSession';
import { readJwtTimeClaims } from 'src/firebase/jwtClaims';

/*
 * BUG-801: the bearer for every request used to be `localStorage.idToken` — a
 * single key shared by every tab of the browser profile. When two accounts
 * signed in on the same profile, the last login overwrote it while an older
 * tab kept rendering the first user's UI, so the browser painted one identity
 * and authenticated as another (403 RequesterUserIsNotPartOfWorkspace / 424 on
 * funnels & leads).
 *
 * The authoritative bearer is now resolved live from THIS tab's Firebase Auth
 * session (getLiveIdToken). The sessionStorage mirror exists only for callers
 * that cannot await — keepalive/pagehide beacons — and to keep traffic flowing
 * while Firebase init has not settled; sessionStorage is per-tab, so another
 * tab's login can never poison it.
 */

export const ID_TOKEN_STORAGE_KEY = 'idToken';

/*
 * Records WHICH identity last wrote the shared identity mirrors and WHEN.
 * Recency has to be written down: it cannot be inferred from where Firebase
 * parked the credential, because persistence ("Recordar mi usuario") and the
 * mirrors' scope are independent axes — the mirrors are always profile-global
 * whatever the checkbox says. See resolveStaleSide.
 */
export const SESSION_OWNER_STORAGE_KEY = 'sessionOwner';

export type tSessionOwner = {
  uid: string;
  loginAt: number;
};

/*
 * The mirror is JSON-stringified to stay byte-compatible with how ahooks'
 * useSessionStorageState serializes the same key in User.hook.ts.
 */
export function readMirroredIdToken(): string {
  return readSessionStoredString(ID_TOKEN_STORAGE_KEY) ?? '';
}

export function writeMirroredIdToken(token: string): void {
  try {
    sessionStorage.setItem(ID_TOKEN_STORAGE_KEY, JSON.stringify(token));
  } catch {
    // Storage unavailable: beacon paths will send unauthenticated and the
    // backend will reject them; axios traffic is unaffected (live token).
  }
}

export function writeSessionOwner(uid: string): void {
  try {
    const owner: tSessionOwner = { uid, loginAt: Date.now() };
    localStorage.setItem(SESSION_OWNER_STORAGE_KEY, JSON.stringify(owner));
  } catch {
    // Storage unavailable — resolveStaleSide degrades to its safe default.
  }
}

export function readSessionOwner(): tSessionOwner | undefined {
  const record = readStoredRecord(SESSION_OWNER_STORAGE_KEY);
  if (!record) return undefined;

  const { uid, loginAt } = record;
  if (typeof uid !== 'string' || uid === '') return undefined;
  if (typeof loginAt !== 'number' || !Number.isFinite(loginAt)) return undefined;

  return { uid, loginAt };
}

/*
 * The profile-global localStorage keys that mirror the signed-in identity.
 * Every recovery path (cross-account reconciliation, the axios 401 handler,
 * onSignOut) MUST purge through this one list — hand-maintained copies are how
 * cross-account residue (a stale currentWorkspace surviving one of the paths)
 * leaks into the next login. `workspaceInfo` and `currentFunnelID` belong here
 * for the same reason: WorkspaceProvider re-seeds its context from
 * `workspaceInfo` at boot without comparing workspace ids, so leftovers would
 * hand a fresh login the previous account's workspace name, role map and
 * isActive flag. The legacy localStorage idToken is here too: this bundle
 * never reads it, but a leftover copy is exactly the poisoned global bearer
 * BUG-801 removes.
 */
const SHARED_SESSION_MIRROR_KEYS = [
  'currentUser',
  'currentUserId',
  'currentWorkspace',
  'currentWorkspaceId',
  'workspaceInfo',
  'currentFunnelID',
  'funnelData',
  SESSION_OWNER_STORAGE_KEY,
  ID_TOKEN_STORAGE_KEY,
];

export function purgePersistedSessionMirrors(): void {
  try {
    SHARED_SESSION_MIRROR_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    // Storage unavailable — nothing persisted to purge.
  }
}

/*
 * Which side of a cross-account mismatch is the stale one — the tab's live
 * credential, or the shared identity mirrors.
 *
 * Decided on recorded recency (the credential's Firebase `auth_time` claim vs
 * the `loginAt` stamped when the mirrors were written), never on where the
 * credential is stored. Storage location says nothing about which session is
 * newer: a tab can hold a durable credential from an old login while another
 * tab writes the mirrors with a brand-new per-tab login, and the reverse
 * happens just as easily.
 *
 * `mirrors` is the default whenever recency cannot be established (no owner
 * record — e.g. mirrors written by a pre-BUG-801 bundle during the rollout
 * window — an owner record that disagrees with the currentUserId mirror, or an
 * unavailable auth_time). Dropping local mirrors costs a re-login; it never
 * destroys a credential, so it is the safe side to be wrong on.
 */
export type tStaleSide = 'tab' | 'mirrors';

export function resolveStaleSide(params: {
  liveAuthTimeMs?: number;
  persistedUserId: string;
  sessionOwner?: tSessionOwner;
}): tStaleSide {
  const { liveAuthTimeMs, persistedUserId, sessionOwner } = params;

  if (!sessionOwner || sessionOwner.uid !== persistedUserId) return 'mirrors';
  if (liveAuthTimeMs === undefined) return 'mirrors';

  return liveAuthTimeMs >= sessionOwner.loginAt ? 'mirrors' : 'tab';
}

/*
 * How long a request waits for Firebase auth to finish restoring the
 * persisted credential before falling back to the per-tab mirror. The SDK's
 * authStateReady() has no rejection wiring — a blocked IndexedDB open or a
 * corrupted profile can stall it forever, and axios's 30s ceiling only covers
 * the adapter, not request interceptors: an unbounded await here would hang
 * EVERY API request in the app with no error and no toast.
 */
const AUTH_STATE_READY_TIMEOUT_MS = 3_000;

/*
 * `degraded` marks a bearer that Firebase did not vouch for on this call — the
 * SDK never settled, loading it failed, or a refresh failed on the network.
 * It may be the per-tab mirror or empty. Callers must not let a degraded 401 trigger the
 * destructive session wipe: a slow boot on a low-end device would otherwise
 * become a profile-wide logout. The axios interceptor sets skipAutoRedirect
 * for exactly this reason.
 */
export type tLiveToken = {
  token: string;
  degraded: boolean;
  /** Diagnostic for this resolution, independent of the document's first outcome. */
  outcome?: tBootOutcome;
};

async function loadAuth() {
  // Firebase is imported dynamically for the same reason axios.singleton.ts
  // does it on the 401 branch: a static import here would initialize the whole
  // Firebase app (auth + firestore + functions) for every module that touches
  // axios, including pre-auth surfaces.
  const { firebase } = await import('./firebase.singleton');
  return firebase.auth;
}

/*
 * Resolves whether Firebase settled its initial auth state within the timeout.
 * Never rejects: a chunk-load failure after a redeploy, or an authStateReady
 * rejection, must not propagate out of the axios request interceptor — an
 * uncaught rejection there fails the request with no `error.response`, so the
 * 401 recovery never engages and the user just sees a dead UI.
 */
async function waitForAuthState(): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const auth = await loadAuth();
    return await Promise.race([
      auth.authStateReady().then(() => true),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(false), AUTH_STATE_READY_TIMEOUT_MS);
      }),
    ]);
  } catch {
    return false;
  } finally {
    // Without this, every API request leaves a dangling 3s timer behind — and
    // any suite using fake timers against this module hangs.
    if (timer) clearTimeout(timer);
  }
}

export type tLiveAuthState = {
  uid?: string;
  authTimeMs?: number;
};

/*
 * The tab's live identity, gated on the same bounded init wait the bearer uses
 * so callers cannot read a null currentUser during the boot race. Returns an
 * empty state when Firebase never settled or holds no credential.
 */
export async function getLiveAuthState(): Promise<tLiveAuthState> {
  if (!(await waitForAuthState())) return {};

  try {
    const auth = await loadAuth();
    const user = auth.currentUser;
    if (!user) return {};

    const result = await user.getIdTokenResult();
    const authTimeMs = new Date(result.authTime).getTime();

    return {
      uid: user.uid,
      authTimeMs: Number.isFinite(authTimeMs) ? authTimeMs : undefined,
    };
  } catch {
    return {};
  }
}

let inFlightToken: Promise<tLiveToken> | undefined;

const WAIT_TIMEOUT = Symbol('auth wait timeout');
const NETWORK_RETRY_DELAYS_MS = [1_000, 2_000, 4_000] as const;

const withinMs = async <T>(pending: Promise<T>, ms: number): Promise<T | typeof WAIT_TIMEOUT> => {
  if (ms <= 0) {
    void pending.catch(() => undefined);
    return WAIT_TIMEOUT;
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      pending,
      new Promise<typeof WAIT_TIMEOUT>((resolve) => {
        timer = setTimeout(() => resolve(WAIT_TIMEOUT), ms);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

type tAuthReady =
  | { kind: 'ready'; auth: Awaited<ReturnType<typeof loadAuth>> }
  | { kind: 'unavailable' };

/*
 * Resolves the bearer from the tab's live Firebase session.
 *
 * No live user (after a SETTLED init) means no bearer — an empty string, not
 * the mirror. A dead credential with a lingering mirrored token is exactly the
 * stale state this fix exists to stop trusting; the resulting 401 lands on the
 * interceptor that already knows how to clean up. A failed refresh returns
 * empty for the same reason: getIdToken() only throws once the cached token is
 * expired AND the refresh failed, so the mirror can only hold that same
 * expired token or an older one — sending it would re-present a credential
 * Firebase just refused to serve. The network case differs: the SDK keeps
 * the user on exactly auth/network-request-failed, so it is "not known yet"
 * rather than a rejected credential. Its request must stay degraded.
 */
async function resolveLiveIdToken(): Promise<tLiveToken> {
  const boot = openBootWindow();
  const remaining = () => Math.max(0, boot.openedAt! + boot.deadlineMs - performance.now());
  const bounded = boot.eligible && boot.arm === 'hold' && remaining() > 0;
  const release = (result: tLiveToken, outcome: tBootOutcome) => {
    boot.outcome = outcome;
    recordBootAuthResolved(outcome);
    return { ...result, outcome };
  };
  const fallback = (outcome: tBootOutcome) =>
    release({ token: readMirroredIdToken(), degraded: true }, outcome);

  const ready: Promise<tAuthReady> = loadAuth().then(async (auth) => {
    await auth.authStateReady();
    if (boot.settledAt === undefined) {
      boot.settledAt = performance.now();
      boot.settledWithUser = Boolean(auth.currentUser);
      recordBootAuthSettled();
    }
    return { kind: 'ready', auth } as const;
  }).catch(() => ({ kind: 'unavailable' } as const));

  let state = await withinMs(ready, bounded ? Math.min(AUTH_STATE_READY_TIMEOUT_MS, remaining()) : AUTH_STATE_READY_TIMEOUT_MS);
  let held = false;
  if (state === WAIT_TIMEOUT) {
    const mirror = readMirroredIdToken();
    const claims = readJwtTimeClaims(mirror);
    const usable = claims?.exp !== undefined && claims.exp > Date.now() / 1_000 + 30;
    if (boot.outcome === undefined && boot.eligible && !usable) {
      boot.cohort = true;
      boot.mirrorState = !mirror ? 'absent' : claims?.exp === undefined ? 'unreadable' : 'expired';
      recordBootAuthPending();
    }
    if (usable) return fallback('mirror');
    if (!bounded) return fallback(boot.eligible ? 'control_3s' : 'ineligible_3s');
    held = true;
    boot.heldAt ??= performance.now();
    state = await withinMs(ready, remaining());
    if (state === WAIT_TIMEOUT) return fallback('deadline');
  }
  if (state.kind === 'unavailable') return fallback('sdk_unavailable');
  const user = state.auth.currentUser;
  if (!user) return release({ token: '', degraded: false }, 'signed_out');
  if ((held || boot.crossAccountDetected) && user.uid !== boot.savedUid) {
    boot.crossAccountDetected = true;
    return release({ token: '', degraded: true }, 'cross_account');
  }

  let retries = 0;
  let networkFailed = false;
  for (;;) {
    if (bounded && remaining() <= 0) {
      return networkFailed
        ? release({ token: '', degraded: true }, 'network_refresh_failed')
        : fallback('deadline');
    }
    try {
      const token = bounded ? await withinMs(user.getIdToken(), remaining()) : await user.getIdToken();
      if (token === WAIT_TIMEOUT) return fallback('deadline');
      return release({ token, degraded: false }, held ? 'held_live' : 'live');
    } catch (error) {
      if (!isNetworkAuthError(error)) return release({ token: '', degraded: false }, 'refresh_rejected');
      networkFailed = true;
      if (!bounded || remaining() <= 0) return release({ token: '', degraded: true }, 'network_refresh_failed');
      let retryTimer: ReturnType<typeof setTimeout> | undefined;
      try {
        const pause = new Promise<void>((resolve) => {
          retryTimer = setTimeout(resolve, NETWORK_RETRY_DELAYS_MS[Math.min(retries, 2)]);
        });
        if (await withinMs(pause, remaining()) === WAIT_TIMEOUT) {
          return release({ token: '', degraded: true }, 'network_refresh_failed');
        }
      } finally {
        if (retryTimer !== undefined) clearTimeout(retryTimer);
      }
      retries += 1;
      boot.retryCount += 1;
    }
  }
}

/*
 * Concurrent requests crossing the token-expiry boundary share one SDK call
 * instead of each re-entering it.
 */
export function getLiveIdToken(): Promise<tLiveToken> {
  if (!inFlightToken) {
    inFlightToken = resolveLiveIdToken().finally(() => {
      inFlightToken = undefined;
    });
  }
  return inFlightToken;
}
